/**
 * Build the client API proxy.
 *
 * Replaces the 329-line preload. The ElectronAPI TypeScript interface still
 * enforces types at compile time — this proxy provides runtime dispatch.
 */

import type { RpcClient } from '@okclawpro/server-core/transport'
import type { ElectronAPI } from '../shared/types'
import {
  CHUNKED_TRANSFER_THRESHOLD,
  invokeChunked,
  prepareChunkedPayload,
} from './chunked-rpc'

// ---------------------------------------------------------------------------
// Channel map entry
// ---------------------------------------------------------------------------

export type ChannelMapEntry =
  | {
      type: 'invoke'
      channel: string
      transform?: (result: any) => any
      timeoutMs?: number
      chunked?: { largeArgIndex: number; thresholdBytes?: number }
    }
  | { type: 'listener'; channel: string }

export type ChannelMap = Record<string, ChannelMapEntry>

interface RpcClientWithInvokeOptions extends RpcClient {
  invokeWithOptions?: (channel: string, options: { timeoutMs?: number } | undefined, ...args: any[]) => Promise<any>
}

// ---------------------------------------------------------------------------
// Proxy builder
// ---------------------------------------------------------------------------

export function buildClientApi(
  client: RpcClient,
  channelMap: ChannelMap,
  isChannelAvailable?: (channel: string) => boolean,
): ElectronAPI {
  const api: Record<string, any> = {}
  const nested: Record<string, Record<string, any>> = {}
  const clientWithInvokeOptions = client as RpcClientWithInvokeOptions

  for (const [key, entry] of Object.entries(channelMap)) {
    let fn: (...a: any[]) => any
    if (entry.type === 'listener') {
      fn = (cb: (...args: any[]) => void) => client.on(entry.channel, cb)
    } else {
      const invokeChannel = async (...args: any[]) => {
        if (entry.chunked) {
          const { largeArgIndex, thresholdBytes = CHUNKED_TRANSFER_THRESHOLD } = entry.chunked
          if (largeArgIndex >= 0 && largeArgIndex < args.length && args[largeArgIndex] != null) {
            const prepared = await prepareChunkedPayload(args[largeArgIndex])
            if (prepared.bytes.length >= thresholdBytes) {
              return invokeChunked(
                clientWithInvokeOptions,
                entry.channel,
                args,
                largeArgIndex,
                undefined,
                prepared,
                entry.timeoutMs != null ? { timeoutMs: entry.timeoutMs } : undefined,
              )
            }
          }
        }

        if (entry.timeoutMs != null && typeof clientWithInvokeOptions.invokeWithOptions === 'function') {
          return clientWithInvokeOptions.invokeWithOptions(
            entry.channel,
            { timeoutMs: entry.timeoutMs },
            ...args,
          )
        }
        return client.invoke(entry.channel, ...args)
      }

      if (entry.transform) {
        const t = entry.transform
        fn = async (...args: any[]) => t(await invokeChannel(...args))
      } else {
        fn = (...args: any[]) => invokeChannel(...args)
      }
    }

    // Dotted keys like "browserPane.create" become nested: api.browserPane.create
    const dotIdx = key.indexOf('.')
    if (dotIdx !== -1) {
      const ns = key.slice(0, dotIdx)
      const method = key.slice(dotIdx + 1)
      if (!nested[ns]) nested[ns] = {}
      nested[ns][method] = fn
    } else {
      api[key] = fn
    }
  }

  // Attach nested namespaces as plain objects
  for (const [ns, methods] of Object.entries(nested)) {
    api[ns] = methods
  }

  // Expose channel availability check for GUI-aware code
  api.isChannelAvailable = isChannelAvailable ?? (() => true)

  return api as ElectronAPI
}
